import { fromJS, List, Map } from "immutable";
import { IStore } from "reducers";
import { TARGETS_ID, TARGET_BAG_ID, TARGET_GROUPS_ID } from "constants/product-collection";
import {
    AllocationBag,
    AllocationBagType,
    generateReconciliationMutations,
    getBagValuesByRowId,
} from "helpers/allocation-dimension";
import {
    bulkMixedSetOperation,
    listInsertOperation,
    listRemoveOperation,
    listSetOperation,
    mixedInsertOperation,
    mixedRemoveOperation,
    MutableType,
    OperationType,
} from "actions/mutate";
import { bulkMutateTargetAllocationBag, RemoveTargetPayload } from "actions/collections/collection-target";
import { TargetGroupWithTargets } from "components/product-collection/footer/types";
import { AjaxError, Observable } from "rxjs";
import { COLLECTIONS } from "constants/action-types";
import { snackbarAdd } from "actions/snackbars";
import { formatMessage } from "lib/globalize-util";
import { push } from "react-router-redux";
import { mapToOperation } from "./mapToOperation";

/**
 * Check if target group is changed
 * TODO: TARGET Add UT
 * @param currentTargetGroup
 * @param updatedTargetGroupWithTargets
 * @returns
 */
export const isTargetGroupChanged = (
    currentTargetGroup: Map<string, any>,
    updatedTargetGroupWithTargets: TargetGroupWithTargets,
) => {
    const isNameChanged = currentTargetGroup.getIn(["struct_value", "name"]) !== updatedTargetGroupWithTargets.name;
    const isMetricChanged =
        currentTargetGroup.getIn(["struct_value", "metric"]) !== updatedTargetGroupWithTargets.metric;

    return isNameChanged || isMetricChanged;
};

/**
 * Generate object mutations by comparing existing Target Groups, Targets and Target Bag objects with updated ones.
 * TODO: TARGET Add UT
 * @param reduxStore
 * @param updatedTargetGroupWithTargets
 * @param updatedTargetBag
 */
export const convertTargetGroupToMutations = (
    reduxStore: IStore,
    updatedTargetGroupWithTargets: TargetGroupWithTargets,
    updatedTargetBag: AllocationBag,
) => {
    const { collectionId } = reduxStore.collection;
    const currentTargetGroupsObject = reduxStore.mutate.snapshots.get(TARGET_GROUPS_ID);
    const currentTargetsObject = reduxStore.mutate.snapshots.get(TARGETS_ID);
    const currentTargetBagObject = reduxStore.mutate.snapshots.get(TARGET_BAG_ID);
    /**
     * Target group mutations
     * 1. If Target Group already exists, use SET operation
     * 2. If Target Group doesn't exists, use INSERT operation
     */
    const targetGroupMutations = [];
    const existingTargetGroupIndex = getTargetGroupIndex(currentTargetGroupsObject, updatedTargetGroupWithTargets.id);

    let insertTargetGroupCount = 0;
    if (existingTargetGroupIndex >= 0) {
        const targetGroupNeedsUpdate = isTargetGroupChanged(
            currentTargetGroupsObject.get(existingTargetGroupIndex),
            updatedTargetGroupWithTargets,
        );

        if (targetGroupNeedsUpdate) {
            const targetGroupAsValue = fromJS({
                struct_value: {
                    id: updatedTargetGroupWithTargets.id,
                    name: updatedTargetGroupWithTargets.name,
                    metric: updatedTargetGroupWithTargets.metric,
                },
            });
            targetGroupMutations.push(listSetOperation(TARGET_GROUPS_ID, existingTargetGroupIndex, targetGroupAsValue));
        }
    } else {
        const targetGroupAsValue = fromJS({
            struct_value: {
                id: updatedTargetGroupWithTargets.id,
                name: updatedTargetGroupWithTargets.name,
                metric: updatedTargetGroupWithTargets.metric,
            },
        });
        targetGroupMutations.push(
            listInsertOperation(
                TARGET_GROUPS_ID,
                targetGroupAsValue,
                currentTargetGroupsObject.size + insertTargetGroupCount,
            ),
        );

        insertTargetGroupCount++;
    }

    /**
     * Targets mutations
     * 1. If Target already exists, we don't need to do anything. Just keep it at current index
     * 2. If Target doesn't exists in existing object but in `updatedTargetGroupWithTargets`, use INSERT operation to the last index
     * 3. If Target exists but not in `updatedTargetGroupWithTargets`, use REMOVE operation
     *
     * To avoid index conflict, it's important to do INSERT operations first and adding them as last items.
     * REMOVE operations should be executed last
     *
     * Eg: Given a list of `item1`, `item2`, `item3`.
     * INSERT `item4` to 2. Resulting in `item1`, `item2`, `item4`, `item3`
     * In the same action, intent to REMOVE `item3`, which is originally at 2, will remove the previously inserted item
     */

    const existingGroupTargets = getTargetsByTargetGroupId(currentTargetsObject, updatedTargetGroupWithTargets.id);

    const remainingTargetIds = [];
    const removedTargetIds = [];

    // Generate remove operations
    const removeTargetOperations = [];
    existingGroupTargets.forEach((existingTarget, existingIndex) => {
        const existingTargetIndex = updatedTargetGroupWithTargets.targets.findIndex(
            (target) => existingTarget.getIn(["struct_value", "id"]) === target.id,
        );

        // Check if existing Target object exists in `updatedTargetGroupWithTargets`. If not, that means we need to remove it
        if (existingTargetIndex < 0) {
            removedTargetIds.push(existingTarget.getIn(["struct_value", "id"]));
            removeTargetOperations.push(
                mixedRemoveOperation(TARGETS_ID, MutableType.List, updatedTargetGroupWithTargets.id, existingIndex),
            );
        }
    });

    // Reversing because we need to remove in reverse order from the backend (index 1 before index 0)
    removeTargetOperations.reverse();

    // Generate insert operations
    const insertTargetOperations = [];
    updatedTargetGroupWithTargets.targets.forEach((target) => {
        const existingTargetIndex = existingGroupTargets.findIndex((existingTarget) => {
            return existingTarget.getIn(["struct_value", "id"]) === target.id;
        });

        if (existingTargetIndex < 0) {
            const newTargetObject = fromJS({
                struct_value: target,
            });
            insertTargetOperations.push(
                mixedInsertOperation(
                    TARGETS_ID,
                    MutableType.List,
                    updatedTargetGroupWithTargets.id,
                    // Add as last item. IMPORTANT: `existingGroupTargets` is immutable object whereas `insertTargetOperations` is JS array
                    existingGroupTargets.size + insertTargetOperations.length - removedTargetIds.length,
                    newTargetObject,
                ),
            );
        }

        // New and existing Target
        remainingTargetIds.push(target.id);
    });

    const targetOperations = [...removeTargetOperations, ...insertTargetOperations];

    /**
     * Logic to update Target bag
     * 1. For removed Targets, clear its Target values
     * 2. For new and existing, sync with latest values
     */
    const targetBagOperations = [];

    // Collect Target bag clear operations
    const clearTargetBagMutation = generateClearBagValuesOperations(removedTargetIds, collectionId);

    if (clearTargetBagMutation) {
        targetBagOperations.push(clearTargetBagMutation);
    }

    // Collect remaining Target bag operations
    remainingTargetIds.forEach((remainingTargetId) => {
        const bagValues = getBagValuesByRowId(AllocationBagType.target, updatedTargetBag, remainingTargetId);
        const sourceBagValues = getBagValuesByRowId(
            AllocationBagType.target,
            currentTargetBagObject,
            remainingTargetId,
        );

        const mutations = generateReconciliationMutations(
            sourceBagValues,
            bagValues,
            remainingTargetId,
            AllocationBagType.target,
        );

        if (mutations.length > 0) {
            targetBagOperations.push(bulkMutateTargetAllocationBag(collectionId, mutations));
        }
    });

    // Return all mutations.
    // Index0: Target group mutations
    // Index1: Target Mutations
    // Index2: Target Bag mutations
    return [targetGroupMutations, targetOperations, targetBagOperations];
};

export const mapOperations = (mutations: any, objectId: string) => {
    return mutations.getIn([objectId, 0]).operations.reduce((acc, operation) => {
        const mappedOperation = mapToOperation(operation);
        const operationType = Reflect.ownKeys(mappedOperation)[0] as string | undefined;

        if (operationType === OperationType.Remove) {
            acc.push({
                [operationType]: mappedOperation[operationType],
            });
            return acc;
        }

        if (operationType) {
            acc.push({
                [operationType]: {
                    ...mappedOperation[operationType],
                    // TODO handle the mutation of targets_groups correctly without the need of checking for struct_value
                    value: mappedOperation[operationType].value.struct_value ?? mappedOperation[operationType].value,
                },
            });
            return acc;
        }

        return acc;
    }, []);
};

export const handleTargetGroupsSyncError = (error: AjaxError, meta: { collectionId: string; objectId: string }) => {
    const actions = [];

    // conflict
    if (error.status === 409) {
        return Observable.of({
            type: COLLECTIONS.COLLECTION.TARGET_METRICS.TARGET_GROUPS.SYNC.CONFLICT,
            meta,
            payload: error,
            error: true,
        });
    }

    // attempting to modify a deleted collection
    if (error.status === 404) {
        actions.push(Observable.of(push("/assortments")));
        actions.push(Observable.of(snackbarAdd({ message: formatMessage("This assortment is no longer available") })));
    }

    // default case
    actions.push(
        Observable.of({
            type: COLLECTIONS.COLLECTION.TARGET_METRICS.TARGET_GROUPS.SYNC.ERROR,
            payload: error,
        }),
    );

    return Observable.merge(
        ...actions,
        Observable.of({
            error: true,
            payload: error,
            type: COLLECTIONS.COLLECTION.ITEMS.SYNC.COMPLETED,
        }),
    );
};

/**
 * Return items from snapshot based on a key
 * @param mutate {Object} mutate object from redux store
 * @param objectId {string} key to get from the snapshot inside mutate object
 */
export const getCurrentItems = (mutate: { snapshots: any }, objectId: string) => {
    const currentItems = mutate.snapshots.get(objectId);
    return currentItems;
};

/**
 * Returns the index of the first matching element from the target groups
 * @param id {string} condition to match from the target groups
 * @param targetGroups {Array} array of target groups
 */
export const getCurrentItemIndex = (id: string, targetGroups: List<any>) => {
    return targetGroups?.toJS().findIndex((group) => (group.struct_value?.id ?? group.id) === id);
};

export const generateTargetRemoveMutations = (
    payload: Omit<RemoveTargetPayload, "queueOptions">,
    reduxStore: IStore,
) => {
    const currentTargetGroupsObject = reduxStore.mutate.snapshots.get(TARGET_GROUPS_ID);
    const currentTargetsObject = reduxStore.mutate.snapshots.get(TARGETS_ID);
    const { collectionId } = reduxStore.collection;

    const { targetGroupId, targetsIds } = payload;

    // Target Groups
    const targetGroupIndex = getTargetGroupIndex(currentTargetGroupsObject, targetGroupId);
    const removeGroupOperation = listRemoveOperation(TARGET_GROUPS_ID, targetGroupIndex);
    const removeGroupOperations = removeGroupOperation ? [removeGroupOperation] : [];

    // Targets
    const targetsFromSnapshot = getTargetsByTargetGroupId(currentTargetsObject, targetGroupId);
    const removeTargetsOperations = generateTargetsRemoveOperations(targetsFromSnapshot, targetsIds, targetGroupId);

    // Target Bag
    const clearBagOperation = generateClearBagValuesOperations(targetsIds, collectionId);
    const clearBagOperations = clearBagOperation ? [clearBagOperation] : [];

    return [clearBagOperations, removeTargetsOperations, removeGroupOperations];
};

export const getTargetGroupIndex = (groups: any[], id: string) => {
    return groups.findIndex(
        // TODO if we fix the struct_value issue, this should use the struct_value key only
        (targetGroup) => (targetGroup.getIn(["struct_value", "id"]) ?? targetGroup.getIn(["id"])) === id,
    );
};

export const generateTargetsRemoveOperations = (
    targetsFromSnapshot: any,
    targetsIdsToRemove: string[],
    targetGroupId: string,
) => {
    const indexesToRemove = targetsIdsToRemove.reduce((acc, idToRemove, c): number[] => {
        const indexOfTargetToRemove = targetsFromSnapshot.findIndex(
            (target) => target.getIn(["struct_value", "id"]) === idToRemove,
        );
        if (indexOfTargetToRemove !== -1) {
            // We are using unshift here to remove them in reverse order to avoid problems with the BE
            acc.unshift(indexOfTargetToRemove);
        }

        return acc;
    }, []);

    const operations = indexesToRemove.map((i) => mixedRemoveOperation(TARGETS_ID, MutableType.List, targetGroupId, i));
    return operations;
};

export const getTargetsByTargetGroupId = (targets: List<any>, groupId: string) => {
    return targets.getIn([groupId, "list", "values"], List([]));
};

export const generateClearBagValuesOperations = (removedTargetsIds: string[], collectionId: string) => {
    const clearOperations = removedTargetsIds.map((removedTargetId) => ({
        bagType: AllocationBagType.target,
        rowId: removedTargetId,
        quantity: 0,
        dimensionValues: [],
    }));

    if (clearOperations.length > 0) {
        const clearOperationsAction = bulkMutateTargetAllocationBag(collectionId, clearOperations);
        return clearOperationsAction;
    }

    return null;
};

// This is only needed to update the timestamps of the whole target group
// since it doesn't have a timestamp of its own
export const createGhostTargetSetOperations = (reduxStore: IStore, groupId: string) => {
    const { mutate } = reduxStore;
    const { snapshots } = mutate;
    const currentTargets = snapshots.get(TARGETS_ID).toJS();
    const targetsKeys = Object.keys(currentTargets);

    const operations = targetsKeys.reduce((acc, targetKey, i) => {
        const targets = currentTargets[targetKey].list.values;
        const firstTarget = currentTargets[targetKey].list.values[0]?.struct_value;

        if (!firstTarget) {
            return acc;
        }

        const targetBelongsToGroup = firstTarget.target_group_id === groupId;
        if (!targetBelongsToGroup) {
            return acc;
        }

        // generating an empty set operation for each target to update their timestamps
        targets.forEach((target, i) => {
            const { modified_at_ms, created_at_ms, name, target_group_id, id, ...validValues } =
                target?.struct_value || {};

            const operation = {
                childKey: groupId,
                type: OperationType.Set,
                index: i,
                value: Map(validValues),
            };

            acc.push(operation);
        });

        return acc;
    }, []);

    return bulkMixedSetOperation(TARGETS_ID, MutableType.List, operations);
};
